import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useContacts } from '../hooks/useContacts';
import { LuHeart } from 'react-icons/lu';
import { useQueryClient } from '@tanstack/react-query';
import { fetchContactById } from '../services/contacts';
import { getRelationshipEmoji } from '../utils/relationshipEmojis';

const RelationshipsPage = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { data: contacts = [], isLoading } = useContacts(accessToken);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState({ key: 'name', direction: 'asc' });
  const [loadingContactId, setLoadingContactId] = useState(null);
  const queryClient = useQueryClient();

  const filteredAndSorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? contacts.filter((c) => {
        const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
        return (
          name.includes(term) ||
          (c.email || '').toLowerCase().includes(term) ||
          (c.phone || '').toLowerCase().includes(term) ||
          (c.met_where || '').toLowerCase().includes(term) ||
          (c.met_when || '').toLowerCase().includes(term)
        );
      })
      : contacts;

    const sorted = [...filtered].sort((a, b) => {
      const dir = sortBy.direction === 'asc' ? 1 : -1;
      const getVal = (c) => {
        switch (sortBy.key) {
          case 'favorite':
            return c.is_favorite ? 1 : 0;
          case 'type':
            return (c.relationship_type || '').toLowerCase();
          case 'met_when':
            return (c.met_when || '').toLowerCase();
          case 'met_where':
            return (c.met_where || '').toLowerCase();
          case 'email':
            return (c.email || '').toLowerCase();
          case 'phone':
            return (c.phone || '').toLowerCase();
          default:
            return `${c.first_name || ''} ${c.last_name || ''}`.trim().toLowerCase();
        }
      };
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });

    return sorted;
  }, [contacts, search, sortBy]);

  const handleSort = (key) => {
    setSortBy((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleViewContact = async (contactId) => {
    if (!accessToken) {
      navigate(`/relationship/${contactId}`);
      return;
    }
    setLoadingContactId(contactId);
    // Prefetch but don't block navigation; detail page uses cached list immediately.
    queryClient
      .prefetchQuery({
        queryKey: ['contact', contactId],
        queryFn: () => fetchContactById(accessToken, contactId),
      })
      .finally(() => setLoadingContactId(null));
    navigate(`/relationship/${contactId}`);
  };

  return (
    <main className="flex h-full flex-1 flex-col px-4 py-6 pt-16 sm:px-6 md:px-8 md:py-10 md:pt-10 overflow-y-auto">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Relationships</p>
          <h1 className="text-2xl font-semibold text-slate-50">Relationships</h1>
        </div>
        <button
          onClick={() => navigate('/relationships/new')}
          className="rounded-xl bg-gradient-to-r from-amber-500 to-sky-500 px-4 py-2 text-sm font-semibold text-[#050815] shadow-lg shadow-sky-500/30"
        >
          + Add Relationship
        </button>
      </header>

          <div className="mb-4 flex items-center gap-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full max-w-lg rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-100 outline-none focus:border-sky-400/60 focus:ring-2 focus:ring-sky-400/30"
              placeholder="Search name, email, phone, where/when met"
            />
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0c1428]/90 shadow-soft">
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm text-slate-100">
                <thead className="border-b border-white/10 text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-3 py-3 text-left">
                      <button type="button" onClick={() => handleSort('favorite')} className="inline-flex items-center gap-1">
                        Fav
                        {sortBy.key === 'favorite' ? (sortBy.direction === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <button type="button" onClick={() => handleSort('name')} className="inline-flex items-center gap-1">
                        Name
                        {sortBy.key === 'name' ? (sortBy.direction === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <button type="button" onClick={() => handleSort('type')} className="inline-flex items-center gap-1">
                        Type
                        {sortBy.key === 'type' ? (sortBy.direction === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <button type="button" onClick={() => handleSort('email')} className="inline-flex items-center gap-1">
                        Email
                        {sortBy.key === 'email' ? (sortBy.direction === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <button type="button" onClick={() => handleSort('phone')} className="inline-flex items-center gap-1">
                        Phone
                        {sortBy.key === 'phone' ? (sortBy.direction === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <button type="button" onClick={() => handleSort('met_when')} className="inline-flex items-center gap-1">
                        Met When
                        {sortBy.key === 'met_when' ? (sortBy.direction === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <button type="button" onClick={() => handleSort('met_where')} className="inline-flex items-center gap-1">
                        Met Where
                        {sortBy.key === 'met_where' ? (sortBy.direction === 'asc' ? '↑' : '↓') : ''}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={7} className="px-3 py-4 text-slate-400">
                        Loading relationships…
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    filteredAndSorted.map((c) => (
                      <tr
                        key={c.id}
                        className="cursor-pointer border-b border-white/5 last:border-0 hover:bg-white/[0.03]"
                        onClick={() => handleViewContact(c.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleViewContact(c.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-busy={loadingContactId === c.id}
                      >
                        <td className="px-3 py-3 text-center">
                          <LuHeart
                            className={`mx-auto h-4 w-4 ${c.is_favorite ? 'fill-rose-400 text-rose-400' : 'text-slate-500'}`}
                            aria-label={c.is_favorite ? 'Favorite' : 'Not favorite'}
                          />
                        </td>
                        <td className="px-3 py-3 font-semibold">
                          {c.first_name} {c.last_name}
                        </td>
                        <td className="px-3 py-3">
                          {c.relationship_type ? (
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-slate-100">
                              <span>{getRelationshipEmoji(c.relationship_type)}</span>
                              <span className="capitalize">{c.relationship_type}</span>
                            </span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-slate-200">{c.email || '—'}</td>
                        <td className="px-3 py-3 text-slate-200">{c.phone || '—'}</td>
                        <td className="px-3 py-3 text-slate-200">{c.met_when || '—'}</td>
                        <td className="px-3 py-3 text-slate-200">{c.met_where || '—'}</td>
                      </tr>
                    ))}
                  {!isLoading && filteredAndSorted.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-4 text-slate-400">
                        No relationships match your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
    </main>
  );
};

export default RelationshipsPage;
